import React, { useRef, useState } from 'react';
import type { NodeInspectorProps } from './types';
import { Coins, AlertTriangle, Tag, X, ArrowDownLeft, ArrowUpRight, Copy, ExternalLink, Shield, ShieldAlert, ShieldCheck, ChevronDown, ChevronRight, DollarSign, Search, KeyRound } from 'lucide-react';
import { DataFreshnessTag } from '../ui/DataFreshnessTag';
import { toast } from 'sonner';
import { safeStr } from '../../utils/safeStr';
import { formatCurrency } from '../../utils/edgeConsolidation';
import { detectChainCandidates } from '../../utils/chainDetect';
import { resolveUsableChain } from '../../utils/canvasSync';
import { addressService } from '../../services/api';
import { computeNextActions } from '../../utils/nextBestAction';
import { computeTemporalCorrelation } from '../../utils/temporalCorrelation';
import { useTransactionStore } from '../../stores/useTransactionStore';

// Helper functions
const normalizeStringArray = (arr: any) => Array.isArray(arr) ? arr : [];
const normalizeAttributions = (arr: any) => Array.isArray(arr) ? arr : [];

const getRiskColor = (score: number) => {
    if (score > 0.7) return { bg: 'bg-[hsl(var(--risk-high))]', border: 'border-red-500/40', text: 'text-red-400', label: 'High Risk' };
    if (score > 0.3) return { bg: 'bg-[hsl(var(--risk-medium))]', border: 'border-amber-500/40', text: 'text-amber-400', label: 'Medium Risk' };    
    return { bg: 'bg-[hsl(var(--risk-low))]', border: 'border-emerald-500/40', text: 'text-emerald-400', label: 'Low Risk' };
};

const CollapsibleSection: React.FC<{ title: string; icon?: React.ReactNode; badge?: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, icon, badge, defaultOpen = true, children }) => {
    const [open, setOpen] = React.useState(defaultOpen);
    return (
        <div className="border-t border-border pt-3">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 w-full text-left mb-2 group"
            >
                {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}     
                {icon}
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold group-hover:text-foreground transition-colors">{title}</span>
                {badge && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">{badge}</span>}
            </button>
            <div
                className="grid transition-[grid-template-rows] duration-200 ease-out"
                style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
            >
                <div className="overflow-hidden">{children}</div>
            </div>
        </div>
    );
};

export const OverviewTab: React.FC<NodeInspectorProps> = (props) => {
    const {
        selectedNode,
        chain,
        linkedUPIs = [],
        upiLinkInput = '',
        setUpiLinkInput,
        handleLinkUPI,
        handleUnlinkUPI,
        upiLinkLoading,
        triageRiskScore,
        isSelectedNodeTriageMatch,
        triageData,
        dossierOverride,
        dossier: injectedDossier,
        traceContext,
        onProbeChain,
    } = props;

    // CHAIN-8: re-probe button session cache — records which addresses we've already probed.
    const probedRef = useRef<Set<string>>(new Set());
    const [probing, setProbing] = useState(false);

    // Subpoena tracker — per-node state for privacy chain investigations
    type SubpoenaStatus = 'not_submitted' | 'submitted' | 'received' | 'declined';
    const SUBPOENA_TARGETS: Array<{ name: string; type: 'exchange' | 'swap'; chains: string[] }> = [
        // Indian exchanges
        { name: 'WazirX', type: 'exchange', chains: ['XMR', 'ZEC', 'DASH', 'BTC', 'ETH', 'TRX'] },
        { name: 'CoinDCX', type: 'exchange', chains: ['XMR', 'ZEC', 'DASH', 'BTC', 'ETH', 'TRX'] },
        { name: 'Zebpay', type: 'exchange', chains: ['BTC', 'ETH', 'TRX', 'ZEC'] },
        { name: 'CoinSwitch Kuber', type: 'exchange', chains: ['BTC', 'ETH', 'TRX', 'XMR', 'ZEC'] },
        { name: 'Giottus', type: 'exchange', chains: ['BTC', 'ETH', 'TRX'] },
        // International exchanges commonly used in India
        { name: 'Binance', type: 'exchange', chains: ['XMR', 'ZEC', 'DASH', 'BTC', 'ETH', 'TRX'] },
        { name: 'KuCoin', type: 'exchange', chains: ['XMR', 'ZEC', 'DASH', 'BTC', 'ETH'] },
        { name: 'OKX', type: 'exchange', chains: ['XMR', 'ZEC', 'DASH', 'BTC', 'ETH'] },
        // Instant swap services (no KYC — subpoena critical)
        { name: 'ChangeNow', type: 'swap', chains: ['XMR', 'ZEC', 'DASH', 'BTC', 'ETH'] },
        { name: 'FixedFloat', type: 'swap', chains: ['XMR', 'ZEC', 'DASH', 'BTC'] },
        { name: 'Exch.cx', type: 'swap', chains: ['XMR', 'ZEC', 'BTC'] },
        { name: 'TradeOgre', type: 'swap', chains: ['XMR', 'ZEC', 'DASH', 'BTC'] },
        { name: 'THORChain (DEX)', type: 'swap', chains: ['BTC', 'ETH', 'DASH'] },
    ];
    const [subpoenaStatuses, setSubpoenaStatuses] = useState<Record<string, { status: SubpoenaStatus; date?: string; notes?: string }>>({});

    // Reset subpoena state when node changes
    React.useEffect(() => {
        setSubpoenaStatuses((selectedNode as any)?._subpoenaStatuses ?? {});
    }, [selectedNode?.id]);

    const updateSubpoenaStatus = (targetName: string, status: SubpoenaStatus) => {
        const updated = {
            ...subpoenaStatuses,
            [targetName]: { ...subpoenaStatuses[targetName], status, date: new Date().toISOString() },
        };
        setSubpoenaStatuses(updated);
        (selectedNode as any)._subpoenaStatuses = updated;
    };

    // Privacy chain view key / tx private key — local state per node
    type EvidenceTier = 'crypto_proof' | 'service_assertion' | 'chain_fact' | 'heuristic' | 'endpoint_artifact';
    const EVIDENCE_TIERS: Array<{ value: EvidenceTier; label: string; color: string }> = [
        { value: 'crypto_proof', label: 'Cryptographic Proof', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
        { value: 'service_assertion', label: 'Service Record', color: 'text-primary bg-primary/10 border-primary/20' },
        { value: 'chain_fact', label: 'On-Chain Fact', color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' },
        { value: 'endpoint_artifact', label: 'Device/Endpoint', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
        { value: 'heuristic', label: 'Heuristic/Inference', color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' },
    ];
    const [privacyKeyInput, setPrivacyKeyInput] = useState('');
    const [privacyKeySource, setPrivacyKeySource] = useState('');  // e.g. "WazirX", "CoinDCX"
    const [privacyKeyCaseRef, setPrivacyKeyCaseRef] = useState('');  // court order / case ref
    const [privacyKeyTier, setPrivacyKeyTier] = useState<EvidenceTier>('crypto_proof');
    const [showKeyMeta, setShowKeyMeta] = useState(false);
    const [savedPrivacyKeys, setSavedPrivacyKeys] = useState<Array<{ key: string; type: string; savedAt: string; source?: string; caseRef?: string; evidenceTier?: EvidenceTier }>>([]);

    // Reset privacy key input when node changes
    React.useEffect(() => {
        setPrivacyKeyInput('');
        setPrivacyKeySource('');
        setPrivacyKeyCaseRef('');
        setPrivacyKeyTier('crypto_proof');
        setShowKeyMeta(false);
        setSavedPrivacyKeys((selectedNode as any)?._privacyKeys ?? []);
    }, [selectedNode?.id]);

    const privacyKeyLabel = selectedNode?.chain === 'XMR'
        ? { placeholder: 'Paste tx private key or view key', typeLabel: 'TX Private Key / View Key' }
        : selectedNode?.chain === 'ZEC'
            ? { placeholder: 'Paste viewing key (zviews... / zviewo...)', typeLabel: 'Viewing Key' }
            : { placeholder: 'Paste decryption key', typeLabel: 'Decryption Key' };

    const handleSavePrivacyKey = () => {
        const trimmed = privacyKeyInput.trim();
        if (!trimmed) return;
        // Infer key type from format
        let keyType = privacyKeyLabel.typeLabel;
        if (selectedNode?.chain === 'XMR') {
            keyType = trimmed.length === 64 ? 'TX Private Key' : 'View Key';
        } else if (selectedNode?.chain === 'ZEC') {
            if (trimmed.startsWith('zviews')) keyType = 'Sapling Viewing Key';
            else if (trimmed.startsWith('zviewo')) keyType = 'Orchard Viewing Key';
            else keyType = 'Viewing Key';
        }
        const newEntry = {
            key: trimmed,
            type: keyType,
            savedAt: new Date().toISOString(),
            source: privacyKeySource.trim() || undefined,
            caseRef: privacyKeyCaseRef.trim() || undefined,
            evidenceTier: privacyKeyTier,
        };
        const updated = [...savedPrivacyKeys, newEntry];
        setSavedPrivacyKeys(updated);
        // Persist on node data so it survives tab switches
        (selectedNode as any)._privacyKeys = updated;
        setPrivacyKeyInput('');
        setPrivacyKeySource('');
        setPrivacyKeyCaseRef('');
        setPrivacyKeyTier('crypto_proof');
        setShowKeyMeta(false);
        toast.success(`${keyType} saved${newEntry.source ? ` (from ${newEntry.source})` : ''}`);
    };

    // Next-best-action computation (privacy chains only)
    const nextActions = React.useMemo(() => {
        if (!(selectedNode as any)?.is_privacy_chain) return [];
        return computeNextActions(selectedNode as any);
    }, [selectedNode?.id, (selectedNode as any)?._privacyKeys?.length, (selectedNode as any)?._subpoenaStatuses]);

    // Temporal correlation (for privacy chain TX nodes with timestamp)
    const getTxStore = useTransactionStore(s => s.getNodeTransactions);

    const handleProbeChain = async () => {
        const addr = selectedNode?.id;
        if (!addr || probing) return;
        const currentChain = resolveUsableChain(addr, selectedNode?.chain, chain);
        probedRef.current.add(addr);
        setProbing(true);
        try {
            const result = await addressService.probeChain(addr);
            const chosen = result.chosen_chain;
            if (chosen && chosen !== currentChain) {
                toast.info(
                    `Chain detected: ${chosen}`,
                    {
                        description: `This address has activity on ${chosen} (reason: ${result.chosen_reason}). Switch the node to ${chosen} to load transactions.`,
                        action: onProbeChain
                            ? { label: `Switch to ${chosen}`, onClick: () => onProbeChain(addr, chosen) }
                            : undefined,
                        duration: 8000,
                    }
                );
            } else if (chosen && chosen === currentChain) {
                toast.success(`Confirmed on ${currentChain}`, { description: `Probe found activity on ${currentChain} — no chain switch needed.` });
            } else {
                const activeChains = Object.entries(result.activity_by_chain || {})
                    .filter(([, v]) => (v as any).has_activity)
                    .map(([c]) => c);
                if (activeChains.length > 0) {
                    toast.info(`Activity found on: ${activeChains.join(', ')}`, {
                        description: `No single chain chosen. Try switching to one of: ${activeChains.join(', ')}.`,
                        duration: 8000,
                    });
                } else {
                    toast.warning('No chain activity found', { description: 'This address has no activity on any supported EVM chain.' });
                }
            }
        } catch {
            toast.error('Chain probe failed', { description: 'Could not contact chain-probe API.' });
        } finally {
            setProbing(false);
        }
    };

    const dossier = dossierOverride || injectedDossier;
    const identityData = dossier?.sections?.identity || {};
    const identityFreshness = identityData?.fetched_at;

    if (!selectedNode) return null;

    const displayedChain = resolveUsableChain(selectedNode.id, selectedNode.chain, chain);
    const displayedRiskScore = selectedNode.risk_score;
    const selectedNodeEntityName = safeStr(selectedNode.entity_name);
    const selectedNodeEntityType = safeStr(selectedNode.entity_type);
    const selectedNodeEntitySource = safeStr(selectedNode.entity_source);
    const riskStyle = displayedRiskScore !== undefined ? getRiskColor(displayedRiskScore) : null;

    const riskVerdict = (() => {
        if (displayedRiskScore === undefined) return null;
        const eName = selectedNodeEntityName;
        const eType = (selectedNodeEntityType || '').toLowerCase();
        const isSanctioned = selectedNode.entity_category === 'sanction' || (selectedNode as any).sanctioned;
        if (isSanctioned) return 'Sanctioned entity — flagged on OFAC/international watchlists. Immediate escalation required.';
        if (eType === 'mixer' || eType === 'tumbler') return `Associated with known mixing service${eName ? ` (${eName})` : ''}. Funds may be obfuscated.`;
        if (eType === 'exchange' && displayedRiskScore <= 0.3) return `Verified exchange deposit address${eName ? ` (${eName})` : ''}. KYC records may be available.`;
        if (eType === 'exchange') return `Exchange-associated address${eName ? ` (${eName})` : ''}. Transaction records obtainable via Section 91 notice.`;
        if (eType === 'hacker' || eType === 'exploit') return `Linked to known exploit/hack${eName ? ` (${eName})` : ''}. High priority for investigation.`;
        if (eType === 'scam' || eType === 'phishing') return `Associated with known scam${eName ? ` (${eName})` : ''}. Likely funds recovery endpoint.`;
        if (eType === 'defi' || eType === 'dex') return `DeFi protocol address${eName ? ` (${eName})` : ''}. Automated smart contract interactions.`;
        if (eType === 'bridge') return `Cross-chain bridge address${eName ? ` (${eName})` : ''}. Funds may have moved to another blockchain.`;      
        if (eType === 'mule') return 'Suspected mule account — intermediary used to layer illicit funds.';
        if (displayedRiskScore > 0.7) return `High-risk unidentified address. Behavioral signals suggest suspicious activity.`;
        if (displayedRiskScore > 0.3) return 'Moderate risk — some indicators warrant further investigation before conclusions.';
        return eName ? `Low-risk identified entity (${eName}). No significant risk indicators detected.` : 'Low-risk address with no significant risk indicators detected.';
    })();

    const copyAddress = () => {
        navigator.clipboard.writeText(selectedNode.id);
        toast.success('Address copied');
    };

    return (
        <div className="text-sm space-y-3 pb-4">
            {/* ——— Privacy Chain Transaction Detail ——— */}
            {(selectedNode as any).is_privacy_chain && (selectedNode as any).type === 'transaction' && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 overflow-hidden">
                    <div className="px-3 py-2 bg-primary/10 border-b border-primary/20 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-primary text-base">&#9888;</span>
                            <span className="text-xs font-semibold text-foreground">
                                {selectedNode.chain === 'XMR' ? 'Monero' : selectedNode.chain === 'ZEC' ? 'Zcash' : selectedNode.chain === 'DASH' ? 'Dash' : selectedNode.chain} Transaction Detected
                            </span>
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                            (selectedNode as any).privacy_chain_tier === 1 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
                        }`}>
                            TIER {(selectedNode as any).privacy_chain_tier}
                        </span>
                    </div>
                    <div className="px-3 py-2.5 space-y-2">
                        {/* Metadata grid */}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                            <div>
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">TX Hash</span>
                                <p className="text-xs font-mono text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                                   onClick={() => { navigator.clipboard.writeText((selectedNode as any).tx_hash || selectedNode.id); toast.success('Hash copied'); }}
                                   title={(selectedNode as any).tx_hash || selectedNode.id}>
                                    {((selectedNode as any).tx_hash || selectedNode.id)?.slice(0, 16)}...
                                </p>
                            </div>
                            <div>
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Block</span>
                                <p className="text-xs font-mono text-foreground">{(selectedNode as any).block_height?.toLocaleString() ?? '—'}</p>
                            </div>
                            <div>
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Timestamp</span>
                                <p className="text-xs text-foreground">
                                    {(selectedNode as any).timestamp
                                        ? new Date(String((selectedNode as any).timestamp)).toLocaleString('en-IN', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'medium' }) + ' UTC'
                                        : '—'}
                                </p>
                            </div>
                            <div>
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Fee</span>
                                <p className="text-xs font-mono text-foreground">
                                    {(selectedNode as any).fee != null ? `${Number((selectedNode as any).fee).toFixed(8)} ${selectedNode.chain}` : '—'}
                                </p>
                            </div>
                            {(selectedNode as any).ring_size != null && (
                                <div>
                                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Ring Size</span>
                                    <p className="text-xs font-mono text-foreground">
                                        {(selectedNode as any).ring_size}
                                        {(selectedNode as any).ring_size >= 16 ? ' (maximum privacy)' : ''}
                                    </p>
                                </div>
                            )}
                            {((selectedNode as any).num_inputs != null || (selectedNode as any).num_outputs != null) && (
                                <div>
                                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Inputs / Outputs</span>
                                    <p className="text-xs font-mono text-foreground">
                                        {(selectedNode as any).num_inputs ?? '?'} / {(selectedNode as any).num_outputs ?? '?'}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* DASH CoinJoin Mixing Depth */}
                        {(selectedNode as any).mixing_depth && (
                            <div className="p-2 rounded bg-amber-500/10 border border-amber-500/20 mt-1">
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-semibold text-amber-300">PrivateSend CoinJoin Detected</p>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                        (selectedNode as any).mixing_depth.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                                        (selectedNode as any).mixing_depth.confidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                        'bg-zinc-500/20 text-zinc-400'
                                    }`}>
                                        {(selectedNode as any).mixing_depth.confidence} confidence
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mb-1.5">
                                    <div>
                                        <span className="text-[9px] text-muted-foreground uppercase">Rounds</span>
                                        <p className="text-sm font-bold text-amber-300">{(selectedNode as any).mixing_depth.estimated_rounds}</p>
                                    </div>
                                    <div>
                                        <span className="text-[9px] text-muted-foreground uppercase">Addressable</span>
                                        <p className="text-sm font-bold text-amber-300">{(selectedNode as any).mixing_depth.addressability_pct}%</p>
                                    </div>
                                    <div>
                                        <span className="text-[9px] text-muted-foreground uppercase">Participants</span>
                                        <p className="text-sm font-bold text-foreground">{(selectedNode as any).mixing_depth.participant_count}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                    <span>Denomination: {(selectedNode as any).mixing_depth.denomination_dash?.toFixed(4)} DASH</span>
                                    {(selectedNode as any).mixing_depth.is_denom_creation && (
                                        <span className="px-1 py-0.5 rounded bg-primary/10 text-primary">Denom creation</span>
                                    )}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">
                                    {(selectedNode as any).mixing_depth.reasoning}
                                </p>
                            </div>
                        )}

                        {/* Hidden data warning */}
                        {(selectedNode as any).addresses_hidden && (
                            <div className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/20 mt-1">
                                <span className="text-red-400 text-sm shrink-0 mt-0.5">&#9940;</span>
                                <div>
                                    <p className="text-xs font-semibold text-red-300">
                                        {(selectedNode as any).amounts_hidden
                                            ? 'Amounts and addresses are HIDDEN'
                                            : 'Addresses are HIDDEN'}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                        {selectedNode.chain === 'XMR'
                                            ? "Monero's RingCT protocol hides sender, receiver, and amounts. On-chain tracing is not possible."
                                            : 'Shielded transaction — sender/receiver not visible on-chain.'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Z-to-T Unshielding Alert — Exit Point Detected */}
                        {(selectedNode as any).is_unshielding && (
                            <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/20 mt-1">
                                <span className="text-amber-400 text-sm shrink-0 mt-0.5">&#9888;</span>
                                <div>
                                    <p className="text-xs font-semibold text-amber-300">Exit Point Detected (Z→T Unshielding)</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5">
                                        {(selectedNode as any).exit_amount != null
                                            ? `${Number((selectedNode as any).exit_amount).toFixed(4)} ZEC exited the shielded pool`
                                            : 'Funds moved from shielded pool to transparent address'}
                                        . The receiving transparent address is now fully traceable — trace it like BTC.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Next Best Action — dynamic suggestions based on node data gaps */}
                        <div className="pt-1 border-t border-primary/15">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Next Best Action</p>
                            <div className="space-y-1.5">
                                {nextActions.slice(0, 4).map((action) => {
                                    const tierColor = action.evidenceType === 'crypto_proof' ? 'text-emerald-400'
                                        : action.evidenceType === 'service_assertion' ? 'text-primary'
                                        : action.evidenceType === 'chain_fact' ? 'text-cyan-400'
                                        : action.evidenceType === 'endpoint_artifact' ? 'text-amber-400'
                                        : 'text-zinc-400';
                                    return (
                                        <div key={action.id} className="bg-primary/5 rounded px-2 py-1.5">
                                            <div className="flex items-start gap-1.5">
                                                <span className={`text-[11px] font-bold shrink-0 mt-0.5 ${tierColor}`}>
                                                    {action.priority <= 1 ? '!!' : action.priority <= 2 ? '!' : '>'}
                                                </span>
                                                <div>
                                                    <p className="text-[11px] text-foreground font-medium">{action.action}</p>
                                                    <p className="text-[10px] text-muted-foreground mt-0.5">{action.reason}</p>
                                                    {action.tool && (
                                                        <p className="text-[9px] text-primary mt-0.5">{action.tool}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {nextActions.length === 0 && (
                                    <p className="text-[11px] text-muted-foreground italic">No additional actions suggested — all key evidence types collected.</p>
                                )}
                            </div>
                        </div>

                        {/* Data sources */}
                        {(selectedNode as any).data_sources && (
                            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                <span>Sources:</span>
                                {((selectedNode as any).data_sources as string[]).map((src: string, i: number) => (
                                    <span key={i} className="px-1.5 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary">
                                        {src}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* View Key / TX Private Key input — XMR, ZEC, and similar privacy chains */}
                        {(selectedNode.chain === 'XMR' || selectedNode.chain === 'ZEC') && (
                            <div className="pt-2 border-t border-primary/15">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <KeyRound className="w-3 h-3 text-primary" />
                                    <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                        {privacyKeyLabel.typeLabel}
                                    </span>
                                </div>

                                {/* Saved keys */}
                                {savedPrivacyKeys.length > 0 && (
                                    <div className="space-y-1 mb-2">
                                        {savedPrivacyKeys.map((entry, idx) => (
                                            <div key={idx} className="bg-primary/10 border border-primary/20 rounded px-2 py-1.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[10px] text-primary font-semibold">{entry.type}</span>
                                                    <div className="flex items-center gap-0.5">
                                                        <button
                                                            onClick={() => { navigator.clipboard.writeText(entry.key); toast.success('Key copied'); }}
                                                            className="text-muted-foreground hover:text-primary p-0.5"
                                                            title="Copy key"
                                                        >
                                                            <Copy className="w-3 h-3" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                const updated = savedPrivacyKeys.filter((_, i) => i !== idx);
                                                                setSavedPrivacyKeys(updated);
                                                                (selectedNode as any)._privacyKeys = updated;
                                                            }}
                                                            className="text-muted-foreground hover:text-red-500 p-0.5"
                                                            title="Remove"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="text-[11px] font-mono text-foreground truncate" title={entry.key}>
                                                    {entry.key.slice(0, 24)}...{entry.key.slice(-8)}
                                                </p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    {entry.source && (
                                                        <span className="text-[9px] px-1 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary">
                                                            Source: {entry.source}
                                                        </span>
                                                    )}
                                                    {entry.caseRef && (
                                                        <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-300">
                                                            Ref: {entry.caseRef}
                                                        </span>
                                                    )}
                                                    {entry.evidenceTier && (() => {
                                                        const tier = EVIDENCE_TIERS.find(t => t.value === entry.evidenceTier);
                                                        return tier ? (
                                                            <span className={`text-[9px] px-1 py-0.5 rounded border ${tier.color}`}>
                                                                {tier.label}
                                                            </span>
                                                        ) : null;
                                                    })()}
                                                    <span className="text-[9px] text-muted-foreground">
                                                        {new Date(entry.savedAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Input */}
                                <div className="flex gap-1">
                                    <input
                                        type="text"
                                        value={privacyKeyInput}
                                        onChange={(e) => setPrivacyKeyInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && !showKeyMeta && handleSavePrivacyKey()}
                                        placeholder={privacyKeyLabel.placeholder}
                                        className="flex-1 bg-secondary border border-border rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                                    />
                                    <button
                                        onClick={handleSavePrivacyKey}
                                        disabled={!privacyKeyInput.trim()}
                                        className="px-2 py-1 bg-primary text-primary-foreground rounded text-[11px] hover:bg-brand-gold-hover disabled:opacity-50"
                                    >
                                        Save
                                    </button>
                                </div>

                                {/* Optional court order metadata toggle */}
                                {privacyKeyInput.trim() && !showKeyMeta && (
                                    <button
                                        onClick={() => setShowKeyMeta(true)}
                                        className="text-[10px] text-primary hover:text-primary mt-1"
                                    >
                                        + Add source / court order reference
                                    </button>
                                )}
                                {showKeyMeta && (
                                    <div className="flex gap-1 mt-1">
                                        <input
                                            type="text"
                                            value={privacyKeySource}
                                            onChange={(e) => setPrivacyKeySource(e.target.value)}
                                            placeholder="Source (e.g. WazirX, CoinDCX)"
                                            className="flex-1 bg-secondary border border-border rounded px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                                        />
                                        <input
                                            type="text"
                                            value={privacyKeyCaseRef}
                                            onChange={(e) => setPrivacyKeyCaseRef(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSavePrivacyKey()}
                                            placeholder="Case ref / court order #"
                                            className="flex-1 bg-secondary border border-border rounded px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                                        />
                                    </div>
                                )}
                                {showKeyMeta && (
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <span className="text-[10px] text-muted-foreground shrink-0">Evidence tier:</span>
                                        <select
                                            value={privacyKeyTier}
                                            onChange={(e) => setPrivacyKeyTier(e.target.value as EvidenceTier)}
                                            className="flex-1 bg-secondary border border-border rounded px-1.5 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                                        >
                                            {EVIDENCE_TIERS.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <p className="text-[10px] text-muted-foreground mt-1">
                                    {selectedNode.chain === 'XMR'
                                        ? 'Paste tx_private_key (64 hex chars) to prove sender, or view key to reveal incoming amounts.'
                                        : 'Paste viewing key from exchange subpoena to decrypt shielded amounts.'}
                                    {' '}Keys are stored locally on this node only.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ——— Subpoena Targets — privacy chain investigations ——— */}
            {(selectedNode as any).is_privacy_chain && (
                <CollapsibleSection
                    title="Subpoena Targets"
                    icon={<Shield className="w-3 h-3 text-muted-foreground" />}
                    defaultOpen={false}
                >
                    <p className="text-[10px] text-muted-foreground mb-2">
                        Track subpoena status for exchanges and swap services that may have records for this {selectedNode.chain} address/transaction.
                    </p>
                    <div className="space-y-1">
                        {SUBPOENA_TARGETS
                            .filter(t => t.chains.includes(selectedNode.chain || ''))
                            .map(target => {
                                const entry = subpoenaStatuses[target.name];
                                const status = entry?.status ?? 'not_submitted';
                                const statusColors: Record<SubpoenaStatus, string> = {
                                    not_submitted: 'bg-zinc-500/10 border-zinc-500/20 text-zinc-400',
                                    submitted: 'bg-primary/10 border-primary/20 text-primary',
                                    received: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
                                    declined: 'bg-red-500/10 border-red-500/20 text-red-400',
                                };
                                const statusLabels: Record<SubpoenaStatus, string> = {
                                    not_submitted: 'Not submitted',
                                    submitted: 'Submitted',
                                    received: 'Received',
                                    declined: 'Declined',
                                };
                                return (
                                    <div key={target.name} className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-secondary/50">
                                        <div className="min-w-0">
                                            <span className="text-[11px] text-foreground font-medium">{target.name}</span>
                                            <span className={`text-[9px] ml-1.5 px-1 py-0.5 rounded ${target.type === 'swap' ? 'bg-amber-500/10 text-amber-400' : 'bg-primary/10 text-primary'}`}>
                                                {target.type === 'swap' ? 'Swap' : 'CEX'}
                                            </span>
                                        </div>
                                        <select
                                            value={status}
                                            onChange={(e) => updateSubpoenaStatus(target.name, e.target.value as SubpoenaStatus)}
                                            className={`text-[10px] px-1.5 py-0.5 rounded border cursor-pointer ${statusColors[status]} bg-transparent focus:outline-none`}
                                        >
                                            {(Object.keys(statusLabels) as SubpoenaStatus[]).map(s => (
                                                <option key={s} value={s}>{statusLabels[s]}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                    </div>
                    <p className="text-[9px] text-muted-foreground mt-2">
                        {selectedNode.chain === 'XMR' ? 'Swap services (ChangeNow, FixedFloat) are the primary XMR on/off-ramp. Request IP logs + deposit/withdrawal records ±2hr window.'
                            : selectedNode.chain === 'ZEC' ? 'Request viewing key disclosure under court order. Exchanges holding ZEC must maintain t-addr records for compliance.'
                            : 'Request deposit/withdrawal records matching transaction timestamp ±30min.'}
                    </p>
                </CollapsibleSection>
            )}

            {/* ——— Risk Badge Strip ——— */}
            {riskStyle && displayedRiskScore !== undefined && (
                <div className={`flex items-center gap-3 p-2.5 rounded-lg border ${riskStyle.bg} ${riskStyle.border}`}>
                    <div className="flex items-center gap-2 flex-1">
                        {displayedRiskScore > 0.7 ? <ShieldAlert className={`w-5 h-5 ${riskStyle.text}`} /> :
                            displayedRiskScore > 0.3 ? <Shield className={`w-5 h-5 ${riskStyle.text}`} /> :
                                <ShieldCheck className={`w-5 h-5 ${riskStyle.text}`} />}
                        <div>
                            <span className={`text-sm font-bold ${riskStyle.text}`}>{(displayedRiskScore * 100).toFixed(0)}%</span>
                            <span className="text-[11px] text-muted-foreground ml-1.5">{riskStyle.label}</span>
                        </div>
                    </div>
                    <div className="flex-1 h-1.5 bg-background/50 rounded-full overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${displayedRiskScore > 0.7 ? 'bg-red-500' : displayedRiskScore > 0.3 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                            style={{ width: `${displayedRiskScore * 100}%` }}
                        />
                    </div>
                    {isSelectedNodeTriageMatch && triageData?.summary?.risk_score !== undefined && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300 whitespace-nowrap">
                            Triage: {Math.round((triageData.summary.risk_score || 0) * 100)}%
                        </span>
                    )}
                </div>
            )}

            {/* ——— Risk Verdict One-Liner (R-INSPECT-01) ——— */}
            {riskVerdict && (
                <div className="px-2 py-1.5 rounded-md bg-secondary/40 border border-border">
                    <p className="text-[11px] text-muted-foreground leading-relaxed">{riskVerdict}</p>
                </div>
            )}

            {/* ——— Trace Path Context ——— */}
            {traceContext && traceContext.length > 0 && (
                <div className="space-y-1">
                    {traceContext.map((tc, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 text-xs">
                            <span className="text-amber-500 font-bold shrink-0">
                                {tc.direction === 'backward' ? '←' : '→'}
                            </span>
                            <span className="text-amber-700 dark:text-amber-300">
                                {tc.direction === 'backward' ? 'Backward' : 'Forward'} trace
                                {' — hop '}
                                <span className="font-mono font-bold">{tc.hopIndex}</span>
                                {' of '}
                                <span className="font-mono font-bold">{tc.totalHops}</span>
                                {tc.entityName && (
                                    <span className="text-muted-foreground">
                                        {tc.direction === 'backward' ? ' from ' : ' to '}
                                        <span className="font-medium text-foreground">{tc.entityName}</span>
                                        {tc.entityType && <span className="text-muted-foreground"> ({tc.entityType})</span>}
                                    </span>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* ——— Address + Entity Name ——— */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Identity</span>
                    {identityFreshness && <DataFreshnessTag fetchedAt={identityFreshness} />}
                </div>
                {selectedNodeEntityName && (
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate flex-1">
                            {selectedNodeEntityName}
                            {selectedNode.entity_country === 'IN' && <span className="ml-1.5 text-xs">🇮🇳</span>}
                        </p>
                        {selectedNodeEntityType && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground whitespace-nowrap">{selectedNodeEntityType}</span>
                        )}
                    </div>
                )}
                {selectedNode.ens_name && (
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-mono text-primary truncate flex-1" title={selectedNode.ens_name}>
                            {selectedNode.ens_name}
                        </p>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary uppercase tracking-wide whitespace-nowrap">
                            {selectedNode.name_service || 'name service'}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-1.5 bg-secondary/40 rounded px-2 py-1.5 border border-border/50">
                    <p className="break-all font-mono text-xs flex-1 text-muted-foreground">{selectedNode.id}</p>
                    <button
                        onClick={copyAddress}
                        className="p-1 hover:bg-muted rounded transition-colors shrink-0"
                        title="Copy address"
                    >
                        <Copy className="w-3 h-3 text-muted-foreground" />
                    </button>
                    <a
                        href={`${displayedChain === 'TRX' ? 'https://tronscan.org/#/address/' : displayedChain === 'BTC' ? 'https://mempool.space/address/' : displayedChain === 'BNB' || displayedChain === 'BSC' ? 'https://bscscan.com/address/' : displayedChain === 'MATIC' || displayedChain === 'POLYGON' ? 'https://polygonscan.com/address/' : 'https://etherscan.io/address/'}${selectedNode.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 hover:bg-muted rounded transition-colors shrink-0"
                        title={`View on ${displayedChain === 'TRX' ? 'TronScan' : displayedChain === 'BTC' ? 'Mempool' : 'Explorer'}`}
                    >
                        <ExternalLink className="w-3 h-3 text-primary" />
                    </a>
                </div>
                {selectedNodeEntitySource && (
                    <p className="text-[10px] text-muted-foreground">Source: {selectedNodeEntitySource}</p>
                )}
            </div>

            {/* ——— Key Metrics Grid ——— */}
            <div className="flex flex-wrap gap-2">
                <div className="flex-1 min-w-[120px] bg-secondary/30 rounded-lg p-2 border border-border/50">
                    <span className="text-[10px] text-muted-foreground block">Chain</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${displayedChain === 'TRX' ? 'text-red-600 dark:text-red-400' :     
                        displayedChain === 'ETH' ? 'text-primary' : 'text-foreground'}`}>
                        {displayedChain === 'TRX' && <Coins className="w-3 h-3" />}
                        {displayedChain || 'Unknown'}
                    </span>
                </div>
                <div className="flex-1 min-w-[120px] bg-secondary/30 rounded-lg p-2 border border-border/50">
                    <span className="text-[10px] text-muted-foreground block">Balance</span>
                    <span className="text-xs font-mono font-medium truncate block" title={selectedNode.balance != null ? String(selectedNode.balance) : undefined}>
                        {selectedNode.balance != null ? formatCurrency(selectedNode.balance, displayedChain || 'ETH') : '—'}
                    </span>
                </div>
                {selectedNode.tx_count != null && (
                    <div className="flex-1 min-w-[120px] bg-secondary/30 rounded-lg p-2 border border-border/50">
                        <span className="text-[10px] text-muted-foreground block">Transactions</span>
                        <span className="text-xs font-mono font-medium">{selectedNode.tx_count?.toLocaleString()}</span>
                    </div>
                )}
                {selectedNode.first_seen && (
                    <div className="flex-1 min-w-[120px] bg-secondary/30 rounded-lg p-2 border border-border/50">
                        <span className="text-[10px] text-muted-foreground block">First Seen</span>
                        <span className="text-xs font-mono">{new Date(Number(selectedNode.first_seen) * 1000).toLocaleDateString()}</span>
                    </div>
                )}
            </div>

            {/* ——— CHAIN-8: Re-probe button for 0-tx ambiguous nodes ——— */}
            {(selectedNode.tx_count === 0 || selectedNode.tx_count == null) &&
                detectChainCandidates(selectedNode.id).length > 1 &&
                !probedRef.current.has(selectedNode.id) && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-primary/30 bg-primary/8 text-blue-700 dark:text-primary">
                    <Search className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] leading-snug">
                            <span className="font-semibold">No transactions loaded.</span>{' '}
                            Format matches {detectChainCandidates(selectedNode.id).join('/')} — the declared chain may be wrong.
                            Probe RPCs to find which chain holds activity (same 0x address is a different entity per EVM chain).
                        </p>
                        <button
                            onClick={handleProbeChain}
                            disabled={probing}
                            className="mt-1.5 flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-primary/40 bg-primary/15 hover:bg-primary/25 disabled:opacity-50 transition-colors"
                        >
                            <Search className="w-3 h-3" />
                            {probing ? 'Probing…' : 'Probe other chains'}
                        </button>
                    </div>
                </div>
            )}

            {/* ——— Labels & Tags ——— */}
            {(() => {
                const rawLabels = [...new Set(normalizeStringArray(selectedNode.node_labels))];
                const labelSet = new Set(rawLabels.map(l => l.toLowerCase().replace(/[\s_-]+/g, '')));
                const nameNorm = (selectedNodeEntityName || '').toLowerCase().replace(/[\s_-]+/g, '');
                if (nameNorm) labelSet.add(nameNorm);
                const rawTags = [...new Set(normalizeStringArray(selectedNode.entity_tags))];
                const filteredTags = rawTags.filter(t => !labelSet.has(t.toLowerCase().replace(/[\s_-]+/g, '')));
                if (rawLabels.length === 0 && filteredTags.length === 0) return null;
                const TAG_CAP = 6;
                const visibleTags = filteredTags.slice(0, TAG_CAP);
                const hiddenTagCount = filteredTags.length - TAG_CAP;
                return (
                    <div className="flex flex-wrap gap-1">
                        {rawLabels.map((lbl: string) => (
                            <span key={`lbl-${lbl}`} className="px-1.5 py-0.5 rounded text-[10px] border border-primary/40 bg-primary/10 text-foreground">
                                {lbl}
                            </span>
                        ))}
                        {visibleTags.map((tagName: string) => (
                            <span key={`tag-${tagName}`} className="px-1.5 py-0.5 rounded text-[10px] border border-slate-400/30 bg-slate-500/10 text-slate-700 dark:text-foreground/80">
                                #{tagName}
                            </span>
                        ))}
                        {hiddenTagCount > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] text-muted-foreground">+{hiddenTagCount} more</span>
                        )}
                    </div>
                );
            })()}

            {/* ——— Attribution Audit ——— */}
            {normalizeAttributions(selectedNode.intelligence_attributions).length > 0 && (
                <CollapsibleSection
                    title="Attribution Audit Trail"
                    badge={`${normalizeAttributions(selectedNode.intelligence_attributions).length} sources`}
                    defaultOpen={!!selectedNodeEntityName}
                >
                    <div className="space-y-1">
                        {normalizeAttributions(selectedNode.intelligence_attributions).slice(0, 5).map((attr: any, idx: number) => (
                            <div key={`${safeStr(attr.key)}-${safeStr(attr.value)}-${idx}`} className="rounded border border-border/70 bg-secondary/30 px-2 py-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-[11px] font-medium leading-tight truncate">{safeStr(attr.key)}: {safeStr(attr.value)}</p>    
                                    {typeof attr.confidence === 'number' && (
                                        <span className="text-[10px] px-1 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                                            {(attr.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                                    src: {safeStr(attr.source) || 'unknown'}
                                    {attr.analyst_or_system ? ` · by ${safeStr(attr.analyst_or_system)}` : ''}
                                    {attr.timestamp ? ` · ${new Date(attr.timestamp).toLocaleDateString()}` : ''}
                                </p>
                            </div>
                        ))}
                        {normalizeAttributions(selectedNode.intelligence_attributions).length > 5 && (
                            <p className="text-[10px] text-muted-foreground">
                                +{normalizeAttributions(selectedNode.intelligence_attributions).length - 5} more
                            </p>
                        )}
                    </div>
                </CollapsibleSection>
            )}

            {/* ——— UPI Aliases ——— */}
            <CollapsibleSection
                title="UPI Aliases"
                icon={<Tag className="w-3 h-3 text-muted-foreground" />}
                badge={linkedUPIs.length > 0 ? `${linkedUPIs.length}` : undefined}
                defaultOpen={linkedUPIs.length > 0}
            >
                {linkedUPIs.length > 0 ? (
                    <div className="space-y-1 mb-2">
                        {linkedUPIs.map((upi: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between bg-primary/10 border border-primary/20 rounded px-2 py-1">
                                <div>
                                    <span className="text-xs font-mono text-foreground">{upi.upi_id}</span>
                                    <span className="text-[10px] text-muted-foreground ml-2">{upi.source}</span>
                                </div>
                                <button
                                    onClick={() => handleUnlinkUPI?.(upi.upi_id)}
                                    className="text-muted-foreground hover:text-red-500 p-0.5"
                                    title="Remove link"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-[11px] text-muted-foreground mb-2">No UPI IDs linked</p>
                )}
                <div className="flex gap-1">
                    <input
                        type="text"
                        value={upiLinkInput}
                        onChange={(e) => setUpiLinkInput?.(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleLinkUPI?.()}
                        placeholder="Add UPI ID (e.g. user@paytm)"
                        className="flex-1 bg-secondary border border-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                    <button
                        onClick={handleLinkUPI}
                        disabled={upiLinkLoading || !upiLinkInput.trim()}
                        className="px-2 py-1 bg-primary text-primary-foreground rounded text-[11px] hover:bg-brand-gold-hover disabled:opacity-50"
                    >
                        {upiLinkLoading ? '...' : 'Link'}
                    </button>
                </div>
            </CollapsibleSection>

            {/* ——— Merchant Profile ——— */}
            {selectedNode.merchant_profile && (
                <CollapsibleSection title="Merchant Profile" defaultOpen={true}>
                    <div className="p-2 bg-primary/5 dark:bg-primary/10 rounded border border-primary/20 dark:border-primary/30">
                        <span className="text-primary block text-xs font-semibold mb-1">
                            🕵 {selectedNode.merchant_profile.user_type}
                        </span>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                            <div>
                                <span className="text-muted-foreground block text-[10px]">Orders</span>
                                <span className="font-mono">{selectedNode.merchant_profile.total_orders?.toLocaleString()}</span>
                            </div>
                            <div>
                                <span className="text-muted-foreground block text-[10px]">Registered</span>
                                <span>{selectedNode.merchant_profile.reg_date}</span>
                            </div>
                        </div>
                        {selectedNode.merchant_profile.reg_date &&
                            new Date(selectedNode.merchant_profile.reg_date).getFullYear() === new Date().getFullYear() && (
                                <div className="mt-1 text-[10px] text-red-600 flex items-center gap-1">
                                    <AlertTriangle className="w-2.5 h-2.5" />
                                    <span>New Account (&lt;1 year)</span>
                                </div>
                            )}
                    </div>
                </CollapsibleSection>
            )}

            {/* ——— Assets & Tokens ——— */}
            {((selectedNode.trc20_balances?.length ?? 0) > 0 || (selectedNode.token_transfers?.length ?? 0) > 0 || (selectedNode.internal_txs?.length ?? 0) > 0) && (
                <CollapsibleSection
                    title="Assets & Tokens"
                    badge={`${(selectedNode.trc20_balances?.length || 0) + (selectedNode.token_transfers?.length || 0)} items`}
                    defaultOpen={true}
                >
                    <div className="space-y-2">
                        {/* TRC20 USDT Balance */}
                        {selectedNode.trc20_balances && selectedNode.trc20_balances.length > 0 && (
                            <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded border border-orange-200 dark:border-orange-800 space-y-1">
                                <span className="text-orange-700 dark:text-orange-300 block text-xs font-semibold mb-1">💰 Token Balances</span>  
                                {selectedNode.trc20_balances.map((token: any, i: number) => (
                                    <div key={i} className="flex justify-between text-xs">
                                        <span>{safeStr(token.token_symbol)}</span>
                                        <span className="font-mono font-medium">{token.balance?.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Token Transfers */}
                        {selectedNode.token_transfers && selectedNode.token_transfers.length > 0 && (
                            <div className="p-2 bg-primary/10 rounded border border-primary/20">        
                                <span className="text-foreground block text-xs font-semibold mb-1">Recent Token Transfers</span>
                                {selectedNode.token_transfers.slice(0, 3).map((tx: any, i: number) => (
                                    <div key={i} className="flex justify-between text-[11px] mb-0.5">
                                        <span className="flex items-center gap-1">
                                            {tx.direction === 'incoming' ? <ArrowDownLeft className="w-2.5 h-2.5 text-green-600" /> : <ArrowUpRight className="w-2.5 h-2.5 text-red-600" />}
                                            {safeStr(tx.token_symbol)}
                                        </span>
                                        <span className={`font-mono ${tx.direction === 'incoming' ? 'text-green-700' : 'text-red-700'}`}>
                                            {tx.direction === 'incoming' ? '+' : '-'}{formatCurrency(tx.value, tx.token_symbol)}
                                        </span>
                                    </div>
                                ))}
                                {selectedNode.token_transfers.length > 3 && (
                                    <div className="text-[10px] text-muted-foreground mt-1">+{selectedNode.token_transfers.length - 3} more</div>   
                                )}
                            </div>
                        )}

                        {/* Internal Transactions */}
                        {selectedNode.internal_txs && selectedNode.internal_txs.length > 0 && (
                            <div className="p-2 bg-gray-50 dark:bg-zinc-900/50 rounded border border-gray-200 dark:border-zinc-800">
                                <span className="text-gray-700 dark:text-gray-300 block text-xs font-semibold mb-1">Internal Txs (Contracts)</span> 
                                {selectedNode.internal_txs.slice(0, 3).map((tx: any, i: number) => (
                                    <div key={i} className="flex justify-between text-[11px] mb-0.5">
                                        <span className="font-mono text-[10px]">{tx.type?.toUpperCase()}</span>
                                        <span className="font-mono">{formatCurrency(tx.value, 'ETH')}</span>
                                    </div>
                                ))}
                                {selectedNode.internal_txs.length > 3 && (
                                    <div className="text-[10px] text-muted-foreground mt-1">+{selectedNode.internal_txs.length - 3} more</div>      
                                )}
                            </div>
                        )}
                    </div>
                </CollapsibleSection>
            )}
        </div>
    );
};
